// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import { type ComponentProps, useCallback, useRef, useState } from "react";

import {
  ZERO_COPY_ELIGIBLE_SOURCE_TYPES,
  type ZeroCopyEligibleSourceType,
} from "@hightouch/lib/warehouse-sharing/eligibility-shared";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useQueryClient } from "react-query";

import {
  Alert,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  CodeSnippet,
  Column,
  ConfirmationDialog,
  Dialog,
  EmptyState,
  MultiSelect,
  Row,
  Spinner,
  Text,
  useToast,
} from "@hightouch/htui";

import { Card } from "src/components/card";
import {
  PermissionedButton,
  PermissionedSwitch,
} from "src/components/permission";
import {
  type WarehouseShareDatabase,
  type WarehouseShareDatabaseStatus,
  type WarehouseShareMirrorQuery,
  type WarehouseShareMirrorStatus,
  type WarehouseShareSetup,
  type WarehouseShareSetupBackgroundJobResultQuery,
  type WarehouseShareValidationCheck,
  useDisableWarehouseShareMutation,
  useRemoveSharedDatabasesMutation,
  useShareDatabasesMutation,
  useStartWarehouseShareSetupBackgroundJobMutation,
  useWarehouseShareAvailableDatabasesQuery,
  useWarehouseShareEligibilityQuery,
  useWarehouseShareMirrorQuery,
  useWarehouseShareSetupBackgroundJobResultQuery,
} from "src/graphql";
import { useGovernedRefetchInterval } from "src/hooks/use-governed-refetch-interval";
import { captureException } from "src/lib/monitoring";
import { toTitleCase } from "src/utils/string";
import { useSchemaDiscovery } from "src/utils/use-schema-discovery";

function toErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** What each platform calls the thing being shared. Exhaustive — no fallback. */
const SHARE_NOUN: Record<
  ZeroCopyEligibleSourceType,
  { singular: string; plural: string }
> = {
  snowflake: { singular: "database", plural: "databases" },
  databricks: { singular: "catalog", plural: "catalogs" },
};

function isEligibleSourceType(
  type: string,
): type is ZeroCopyEligibleSourceType {
  return (ZERO_COPY_ELIGIBLE_SOURCE_TYPES as readonly string[]).includes(type);
}

type Props = {
  sourceId: string;
  sourceType: string;
};

type SectionProps = {
  sourceId: string;
  sourceType: ZeroCopyEligibleSourceType;
  isEligible: boolean;
  eligibilityReason: string | null | undefined;
  isEligibilityLoading: boolean;
  eligibilityError?: string;
  eligibilityWarning?: string;
  onRetryEligibility: () => void;
};

type BadgeVariant = NonNullable<ComponentProps<typeof Badge>["variant"]>;

type Mirror = NonNullable<WarehouseShareMirrorQuery["warehouseShareMirror"]>;

type SetupPollMessage = {
  kind: "failed" | "transient";
  message: string;
};

function statusBadge(status: WarehouseShareMirrorStatus | null | undefined): {
  variant: BadgeVariant;
  label: string;
} {
  switch (status) {
    case "active":
      return { variant: "success", label: "Active" };
    case "degraded":
      return { variant: "warning", label: "Degraded" };
    case "error":
      return { variant: "error", label: "Error" };
    case "provisioning":
      return { variant: "warning", label: "Provisioning" };
    default:
      return { variant: "subtle", label: "Inactive" };
  }
}

/** Per-share database status badge. */
function shareStatusBadge(status: WarehouseShareDatabaseStatus): {
  variant: BadgeVariant;
  label: string;
} {
  switch (status) {
    case "active":
      return { variant: "success", label: "Active" };
    case "pending":
      return { variant: "warning", label: "Provisioning" };
    case "error":
      return { variant: "error", label: "Error" };
    case "removing":
      return { variant: "subtle", label: "Removing" };
  }
}

/**
 * Poll only mid-transition. `active`/`error` are terminal here, so a settled
 * share stops polling; a background reconcile recovery is picked up on the next
 * mount rather than by a permanent timer.
 */
function shouldPollMirror(
  data: WarehouseShareMirrorQuery | undefined,
): boolean {
  const databases = data?.warehouseShareMirror?.databases;
  if (!databases) return false;
  return databases.some(
    (d) => d.status === "pending" || d.status === "removing",
  );
}

export const ZeroCopySharing = ({ sourceId, sourceType }: Props) => {
  const { zeroCopyWarehouseSharingEnabled } = useFlags();
  const eligibleSourceType = isEligibleSourceType(sourceType);
  const {
    data: eligibilityData,
    isLoading: isEligibilityLoading,
    isError: eligibilityError,
    error: eligibilityQueryError,
    refetch: refetchEligibility,
  } = useWarehouseShareEligibilityQuery(
    { connectionId: sourceId },
    {
      enabled: Boolean(zeroCopyWarehouseSharingEnabled) && eligibleSourceType,
    },
  );
  // The client gates the feature flag and known UI platforms; the server owns
  // region and credential eligibility so it can keep warehouse_info private.
  if (!zeroCopyWarehouseSharingEnabled) {
    return null;
  }
  if (!isEligibleSourceType(sourceType)) {
    return null;
  }
  // Mount for eligible types so region blockers show as alerts.
  return (
    <ZeroCopySharingSection
      sourceId={sourceId}
      sourceType={sourceType}
      isEligible={eligibilityData?.warehouseShareEligibility.eligible === true}
      eligibilityReason={eligibilityData?.warehouseShareEligibility.reason}
      isEligibilityLoading={isEligibilityLoading}
      eligibilityError={
        eligibilityError && !eligibilityData
          ? toErrorMessage(eligibilityQueryError)
          : undefined
      }
      eligibilityWarning={
        eligibilityError && eligibilityData
          ? toErrorMessage(eligibilityQueryError)
          : undefined
      }
      onRetryEligibility={() => void refetchEligibility()}
    />
  );
};

const ZeroCopySharingSection = ({
  sourceId,
  sourceType,
  isEligible,
  eligibilityReason,
  isEligibilityLoading,
  eligibilityError,
  eligibilityWarning,
  onRetryEligibility,
}: SectionProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const mirrorQueryKey = useWarehouseShareMirrorQuery.getKey({
    connectionId: sourceId,
  });
  const noun = SHARE_NOUN[sourceType];
  const shareNoun = noun.plural;

  // Null means the last shared database was removed.
  const cacheMirror = async (mirror: Mirror | null) => {
    await queryClient.cancelQueries(mirrorQueryKey);
    queryClient.setQueryData<WarehouseShareMirrorQuery>(mirrorQueryKey, {
      warehouseShareMirror: mirror,
    });
  };

  const [selectedDatabases, setSelectedDatabases] = useState<string[]>([]);
  const [showSetup, setShowSetup] = useState(false);
  const [stagedForRemove, setStagedForRemove] = useState<string[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);

  const { refetchInterval: governedRefetchInterval, resetBackoff } =
    useGovernedRefetchInterval<WarehouseShareMirrorQuery>({
      baseMs: 5000,
      compareKey: (data) => [
        data?.warehouseShareMirror?.status,
        ...(data?.warehouseShareMirror?.databases ?? []).map(
          (d) => `${d.name}:${d.status}`,
        ),
      ],
    });

  const {
    data: mirrorData,
    isLoading: mirrorLoading,
    isError: mirrorError,
    error: mirrorQueryError,
    refetch: refetchMirror,
  } = useWarehouseShareMirrorQuery(
    { connectionId: sourceId },
    {
      refetchInterval: (data, query) =>
        shouldPollMirror(data) ? governedRefetchInterval(data, query) : false,
    },
  );
  const mirror = mirrorData?.warehouseShareMirror;
  const mirrorUnavailable = mirrorError && !mirrorData;
  const status = mirror?.status ?? null;
  const isEnabled = status === "active" || status === "degraded";
  const databases = mirror?.databases ?? [];
  const hasChildren = databases.length > 0;
  const tearingDown =
    hasChildren && databases.every((d) => d.status === "removing");

  const {
    data: available,
    isLoading: availableLoading,
    isError: availableError,
    error: availableQueryError,
    refetch: refetchAvailableDatabases,
  } = useWarehouseShareAvailableDatabasesQuery(
    { connectionId: sourceId },
    {
      enabled: showSetup,
      select: (data) => data.warehouseShareAvailableDatabases.databases ?? [],
    },
  );

  // Validation runs in the customer-data worker and is polled like source testing.
  const [setupJobId, setSetupJobId] = useState<string | undefined>();
  const [setup, setSetup] = useState<WarehouseShareSetup | undefined>();
  const [setupFetching, setSetupFetching] = useState(false);
  const [setupPollMessage, setSetupPollMessage] = useState<SetupPollMessage>();
  const startSetupJob = useStartWarehouseShareSetupBackgroundJobMutation({
    // Setup validation creates a job but does not change mirror state or the
    // databases the picker lists, so avoid a needless warehouse re-list.
    onSuccess: () => undefined,
  });
  const {
    refetchInterval: setupRefetchInterval,
    resetBackoff: resetSetupPollingBackoff,
  } = useGovernedRefetchInterval({
    baseMs: 250,
    maxMs: 5000,
    unchangedPollsBeforeBackoff: 0,
    jitter: 0,
    compareKey: (
      data: WarehouseShareSetupBackgroundJobResultQuery | undefined,
    ) => data?.warehouseShareSetupBackgroundJobResult.__typename,
  });
  // Generation counter: without it a superseded run's job id could win the race
  // and we'd poll validation for a selection the user no longer has.
  const setupRunRef = useRef(0);
  const lastStartedSelectionKeyRef = useRef<string>();

  const startSetupValidation = useCallback(
    async (databases: string[]) => {
      lastStartedSelectionKeyRef.current = databases.join("\0");
      const run = ++setupRunRef.current;
      const isCurrent = () => setupRunRef.current === run;

      setSetup(undefined);
      setSetupJobId(undefined);
      setSetupPollMessage(undefined);
      if (databases.length === 0) {
        setSetupFetching(false);
        return;
      }
      setSetupFetching(true);
      resetSetupPollingBackoff();
      try {
        const started = await startSetupJob.mutateAsync({
          connectionId: sourceId,
          databases,
        });
        if (!isCurrent()) return;
        const jobId =
          started.startWarehouseShareSetupBackgroundJob?.jobId ?? undefined;
        if (!jobId) {
          throw new Error("Share setup job did not return a job id");
        }
        setSetupJobId(jobId);
      } catch (error) {
        captureException(error);
        if (!isCurrent()) return;
        setSetupFetching(false);
        toast({
          id: "zero-copy-setup",
          title: "Couldn't validate share setup",
          message: toErrorMessage(error),
          variant: "error",
        });
      }
    },
    [resetSetupPollingBackoff, sourceId, startSetupJob, toast],
  );

  const resetSetupValidation = () => {
    lastStartedSelectionKeyRef.current = undefined;
    setupRunRef.current += 1;
    setSetupJobId(undefined);
    setSetup(undefined);
    setSetupFetching(false);
    setSetupPollMessage(undefined);
  };

  const handleDatabaseSelectionChange = (databases: string[]) => {
    resetSetupValidation();
    setSelectedDatabases(databases);
  };

  const handleDatabasePickerClose = () => {
    const selectionKey = selectedDatabases.join("\0");
    if (
      selectedDatabases.length > 0 &&
      lastStartedSelectionKeyRef.current !== selectionKey
    ) {
      void startSetupValidation(selectedDatabases);
    }
  };

  const closeSetup = () => {
    resetSetupValidation();
    setSelectedDatabases([]);
    setShowSetup(false);
  };

  useWarehouseShareSetupBackgroundJobResultQuery(
    { jobId: setupJobId! },
    {
      enabled: Boolean(setupJobId),
      refetchInterval: setupRefetchInterval,
      onSuccess(data) {
        const result = data.warehouseShareSetupBackgroundJobResult;
        switch (result.__typename) {
          case "WarehouseShareSetupBackgroundJobProcessing":
            setSetupPollMessage(undefined);
            break;
          case "WarehouseShareSetupBackgroundJobFailure": {
            setSetupJobId(undefined);
            setSetupFetching(false);
            setSetup(undefined);
            setSetupPollMessage({ kind: "failed", message: result.error });
            toast({
              id: "zero-copy-setup",
              title: "Couldn't validate share setup",
              message: result.error,
              variant: "error",
            });
            break;
          }
          case "WarehouseShareSetupBackgroundJobSuccess": {
            setSetupJobId(undefined);
            setSetupFetching(false);
            setSetupPollMessage(undefined);
            setSetup(result.result);
            break;
          }
        }
      },
      onError(error) {
        captureException(error);
        setSetupPollMessage({
          kind: "transient",
          message:
            "Connection interrupted while checking setup. Retrying automatically.",
        });
      },
    },
  );

  const share = useShareDatabasesMutation();
  const disable = useDisableWarehouseShareMutation();
  const remove = useRemoveSharedDatabasesMutation();

  // Exclude already-shared databases from the picker — re-provisioning an
  // imported DB fails ("Granting individual privileges on imported database is
  // not allowed"). Case-insensitive (shares keys vs discovered names).
  const sharedLower = new Set(databases.map((d) => d.name.toLowerCase()));
  const addableDatabases = (available ?? []).filter(
    (db) => !sharedLower.has(db.toLowerCase()),
  );

  // Additive, so this is both the first-time enable and a later add.
  const handleShare = async () => {
    const firstShare = !isEnabled;
    try {
      const result = await share.mutateAsync({
        connectionId: sourceId,
        databases: selectedDatabases,
      });
      await cacheMirror(result.shareDatabases);
      // Let the provisioning state replace setup after a successful request.
      closeSetup();
      // Reset polling to promptly show the next status change.
      resetBackoff();
      toast({
        id: "zero-copy-share",
        title: firstShare
          ? "Provisioning zero-copy sharing"
          : `Adding ${shareNoun} to the share`,
        message: `Hightouch is setting up the share. This can take several minutes per ${noun.singular}.`,
        variant: "success",
      });
    } catch (error) {
      captureException(error);
      toast({
        id: "zero-copy-share",
        title: firstShare
          ? "Couldn't enable zero-copy sharing"
          : `Couldn't add ${shareNoun}`,
        message: toErrorMessage(error),
        variant: "error",
      });
    }
  };

  const removableNames = new Set(
    databases.filter((d) => d.status !== "removing").map((d) => d.name),
  );
  const effectiveStagedForRemove = stagedForRemove.filter((name) =>
    removableNames.has(name),
  );

  const openDeleteDialog = () => {
    setStagedForRemove([]);
    setShowDeleteDialog(true);
  };

  const closeDeleteDialog = () => {
    setShowDeleteDialog(false);
    setStagedForRemove([]);
  };

  const handleRemoveStaged = async () => {
    if (effectiveStagedForRemove.length === 0) return;
    const removing = [...effectiveStagedForRemove];
    try {
      const result = await remove.mutateAsync({
        connectionId: sourceId,
        databases: removing,
      });
      await cacheMirror(result.removeSharedDatabases);
      closeDeleteDialog();
      closeSetup();
      // Reset polling to promptly show removed databases.
      resetBackoff();
      toast({
        id: "zero-copy-remove",
        title: `Removing ${removing.length} ${
          removing.length === 1 ? noun.singular : noun.plural
        }`,
        message: `Hightouch is tearing down the share for the selected ${shareNoun}.`,
        variant: "success",
      });
    } catch (error) {
      captureException(error);
      toast({
        id: "zero-copy-remove",
        title: `Couldn't remove ${shareNoun}`,
        message: toErrorMessage(error),
        variant: "error",
      });
    }
  };

  const toggleStagedForRemove = (database: string, staged: boolean) => {
    setStagedForRemove((prev) => {
      if (staged) {
        return prev.includes(database) ? prev : [...prev, database];
      }
      return prev.filter((name) => name !== database);
    });
  };

  const removableDatabases = databases.filter((d) => d.status !== "removing");

  const handleDisable = async () => {
    try {
      const result = await disable.mutateAsync({ connectionId: sourceId });
      await cacheMirror(result.disableWarehouseShare);
      resetBackoff();
      toast({
        id: "zero-copy-disable",
        title: "Disabling zero-copy sharing",
        message:
          "Hightouch has stopped routing queries through the mirror and is tearing it down.",
        variant: "success",
      });
    } catch (error) {
      captureException(error);
      toast({
        id: "zero-copy-disable",
        title: "Couldn't disable zero-copy sharing",
        message: toErrorMessage(error),
        variant: "error",
      });
    }
  };

  const badge = statusBadge(status);
  // Show a spinner while pending mirror state reaches the client.
  const provisioning = status === "provisioning" || share.isLoading;
  const engaged = isEnabled || provisioning || hasChildren || tearingDown;
  const permission = {
    v2: {
      resource: "source" as const,
      grant: "can_update" as const,
      id: Number(sourceId),
    },
  };

  const confirmLabel = isEnabled ? `Add ${shareNoun}` : "Enable sharing";
  const canEnableOrAdd =
    !isEligibilityLoading &&
    !eligibilityError &&
    !mirrorUnavailable &&
    isEligible;
  const isSwitchChecked = hasChildren && !tearingDown;

  return (
    <Card
      gap={4}
      heading="Zero-copy sharing"
      description="Let Hightouch query a zero-copy share of your data on Hightouch compute instead of your warehouse."
      rightContent={
        <Row align="center" gap={3}>
          {engaged && mirror && (
            <Badge variant={badge.variant}>
              {tearingDown ? "Removing" : badge.label}
            </Badge>
          )}
          {showSetup && <Badge variant="warning">Setup in progress</Badge>}
          <PermissionedSwitch
            aria-label="Enable zero-copy sharing"
            permission={permission}
            isChecked={isSwitchChecked}
            isDisabled={
              mirrorLoading ||
              mirrorUnavailable ||
              share.isLoading ||
              disable.isLoading ||
              tearingDown ||
              (!canEnableOrAdd && !engaged)
            }
            onChange={(checked) => {
              if (checked) {
                if (!canEnableOrAdd) return;
                setShowSetup(true);
              } else {
                closeSetup();
                if (isEnabled || hasChildren) {
                  setConfirmDisable(true);
                }
              }
            }}
          />
        </Row>
      }
    >
      {eligibilityError ? (
        <RetryableAlert
          title="Couldn’t check zero-copy eligibility"
          message={eligibilityError}
          onRetry={onRetryEligibility}
        />
      ) : eligibilityReason === "missing_warehouse_region" ? (
        <MissingRegionAlert sourceId={sourceId} permission={permission} />
      ) : eligibilityReason ? (
        <Alert
          type="warning"
          title="Zero-copy sharing unavailable"
          message="Zero-copy sharing is not available for this source."
          actions={
            <Button variant="secondary" onClick={onRetryEligibility}>
              Retry
            </Button>
          }
        />
      ) : null}
      {eligibilityWarning && (
        <RetryableAlert
          title="Couldn’t refresh zero-copy eligibility"
          message={eligibilityWarning}
          onRetry={onRetryEligibility}
        />
      )}

      {mirrorUnavailable ? (
        <RetryableAlert
          title="Couldn’t load sharing status"
          message={toErrorMessage(mirrorQueryError)}
          onRetry={() => void refetchMirror()}
        />
      ) : mirrorLoading ? (
        <Row justify="center" p={4}>
          <Box role="status" aria-label="Loading zero-copy sharing status">
            <Spinner />
          </Box>
        </Row>
      ) : provisioning && !isEnabled ? (
        <Column gap={4}>
          <Row align="center" justify="center" gap={2} p={4}>
            <Box
              flexShrink={0}
              role="status"
              aria-label="Setting up zero-copy sharing"
            >
              <Spinner size="sm" />
            </Box>
            <Text color="text.secondary">
              Setting up the share — this can take several minutes per{" "}
              {noun.singular}.
            </Text>
          </Row>
          {hasChildren && (
            <ActiveState noun={shareNoun} databases={databases} />
          )}
          {mirrorError && (
            <RetryableAlert
              title="Couldn’t refresh sharing status"
              message={toErrorMessage(mirrorQueryError)}
              onRetry={() => void refetchMirror()}
            />
          )}
        </Column>
      ) : isEnabled || showSetup || hasChildren ? (
        <Column gap={4}>
          {mirrorError && (
            <RetryableAlert
              title="Couldn’t refresh sharing status"
              message={toErrorMessage(mirrorQueryError)}
              onRetry={() => void refetchMirror()}
            />
          )}
          {hasChildren && (
            <ActiveState noun={shareNoun} databases={databases} />
          )}

          {hasChildren &&
            !tearingDown &&
            (canEnableOrAdd || removableDatabases.length > 0) && (
              <Row gap={2}>
                {canEnableOrAdd && !showSetup && (
                  <PermissionedButton
                    permission={permission}
                    onClick={() => setShowSetup(true)}
                  >
                    Add {shareNoun}
                  </PermissionedButton>
                )}
                {removableDatabases.length > 0 && (
                  <PermissionedButton
                    permission={permission}
                    variant="secondary"
                    isDisabled={remove.isLoading}
                    onClick={openDeleteDialog}
                  >
                    Delete {shareNoun}
                  </PermissionedButton>
                )}
              </Row>
            )}

          {showSetup && (
            <SetupState
              picker={{
                noun: shareNoun,
                availableDatabases: addableDatabases,
                availableLoading,
                availableError:
                  availableError && !available
                    ? toErrorMessage(availableQueryError)
                    : undefined,
                availableWarning:
                  availableError && available
                    ? toErrorMessage(availableQueryError)
                    : undefined,
                onRetry: () => void refetchAvailableDatabases(),
                selectedDatabases,
                onChange: handleDatabaseSelectionChange,
                onClose: handleDatabasePickerClose,
              }}
              setup={setup}
              setupFetching={setupFetching}
              setupPollMessage={setupPollMessage}
              onRevalidate={() => {
                void startSetupValidation(selectedDatabases);
              }}
              onConfirm={handleShare}
              confirmLabel={confirmLabel}
              isConfirming={share.isLoading}
              isConfirmDisabled={!canEnableOrAdd || !setup?.ready}
              onCancel={closeSetup}
              permission={permission}
            />
          )}
        </Column>
      ) : (
        <EmptyState
          variant="minimal"
          title="Zero-copy sharing is off"
          message="Share data with Hightouch"
        />
      )}

      <Dialog
        isOpen={showDeleteDialog}
        variant="form"
        width="md"
        title={`Delete shared ${shareNoun}`}
        onClose={closeDeleteDialog}
        actions={
          <ButtonGroup>
            <Button
              variant="secondary"
              isDisabled={remove.isLoading}
              onClick={closeDeleteDialog}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              isDisabled={effectiveStagedForRemove.length === 0}
              isLoading={remove.isLoading}
              onClick={() => void handleRemoveStaged()}
            >
              Delete
            </Button>
          </ButtonGroup>
        }
      >
        <Column gap={3}>
          <Text color="text.secondary">
            Select the {shareNoun} to remove from the zero-copy share. Hightouch
            will stop routing queries through their mirrors and tear them down.
            Your warehouse data is not affected.
          </Text>
          <Column gap={2}>
            {removableDatabases.map((entry) => (
              <Checkbox
                key={entry.name}
                label={entry.name}
                isChecked={effectiveStagedForRemove.includes(entry.name)}
                isDisabled={remove.isLoading}
                onChange={(event) =>
                  toggleStagedForRemove(entry.name, event.target.checked)
                }
              />
            ))}
          </Column>
        </Column>
      </Dialog>

      <ConfirmationDialog
        isOpen={confirmDisable}
        title="Disable zero-copy sharing"
        confirmButtonText="Disable and remove"
        variant="danger"
        onClose={() => setConfirmDisable(false)}
        onConfirm={async () => {
          setConfirmDisable(false);
          await handleDisable();
        }}
      >
        <Text>
          Disable zero-copy sharing for this source? Hightouch will tear down
          every shared {noun.singular} and stop routing queries through the
          mirror. Your warehouse data is not affected.
        </Text>
      </ConfirmationDialog>
    </Card>
  );
};

type SetupStateProps = {
  picker: DatabasePickerProps;
  setup: WarehouseShareSetup | undefined;
  setupFetching: boolean;
  setupPollMessage?: SetupPollMessage;
  onRevalidate: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  isConfirming: boolean;
  isConfirmDisabled: boolean;
  onCancel?: () => void;
  permission: ComponentProps<typeof PermissionedButton>["permission"];
};

const SetupState = ({
  picker,
  setup,
  setupFetching,
  setupPollMessage,
  onRevalidate,
  onConfirm,
  confirmLabel,
  isConfirming,
  isConfirmDisabled,
  onCancel,
  permission,
}: SetupStateProps) => {
  const customerCommands = setup?.customerCommands ?? [];
  const checks: WarehouseShareValidationCheck[] = setup?.checks ?? [];
  return (
    <Column gap={4}>
      <DatabasePicker {...picker} />

      {setupPollMessage && (
        <Alert
          type={setupPollMessage.kind === "failed" ? "error" : "warning"}
          title={
            setupPollMessage.kind === "failed"
              ? "Setup validation failed"
              : "Setup validation delayed"
          }
          message={setupPollMessage.message}
        />
      )}

      {customerCommands.length > 0 && (
        <Column gap={2}>
          <Text fontWeight="medium">Run this SQL in your warehouse</Text>
          <CodeSnippet code={customerCommands.map((c) => `${c};`).join("\n")} />
        </Column>
      )}

      {checks.length > 0 && (
        <Column gap={2}>
          <Text fontWeight="medium">Validation</Text>
          {checks.map((check) => (
            <Row key={check.name} align="flex-start" gap={2}>
              <Badge variant={check.passed ? "success" : "error"}>
                {check.passed ? "Passed" : "Failed"}
              </Badge>
              <Text size="sm">{check.name}</Text>
              {check.error && (
                <Text
                  size="sm"
                  color="text.secondary"
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                >
                  {check.error}
                </Text>
              )}
            </Row>
          ))}
        </Column>
      )}

      <Row gap={2}>
        {picker.selectedDatabases.length > 0 && (
          <>
            <Button isLoading={setupFetching} onClick={onRevalidate}>
              Re-validate
            </Button>
            <PermissionedButton
              permission={permission}
              variant="primary"
              isDisabled={isConfirmDisabled}
              isLoading={isConfirming}
              onClick={onConfirm}
            >
              {confirmLabel}
            </PermissionedButton>
          </>
        )}
        {/* Cancel remains available so setup can be closed before selection. */}
        {onCancel && (
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </Row>
    </Column>
  );
};

type ActiveStateProps = {
  noun: string;
  databases: WarehouseShareDatabase[];
};

const ActiveState = ({ noun, databases }: ActiveStateProps) => {
  return (
    <Column gap={2}>
      <Text fontWeight="medium">Shared {noun}</Text>
      <Column gap={2}>
        {databases.map((entry) => {
          const badge = shareStatusBadge(entry.status);
          return (
            <Row key={entry.name} align="flex-start" gap={2}>
              <Text fontWeight="medium">{entry.name}</Text>
              <Badge variant={badge.variant}>{badge.label}</Badge>
              {entry.error && (
                <Text
                  size="sm"
                  color="text.secondary"
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                >
                  {entry.error}
                </Text>
              )}
            </Row>
          );
        })}
      </Column>
    </Column>
  );
};

type DatabasePickerProps = {
  /** Plural noun for what's shared: "databases" (Snowflake) / "catalogs" (Databricks). */
  noun: string;
  availableDatabases: string[];
  availableLoading: boolean;
  availableError?: string;
  availableWarning?: string;
  onRetry: () => void;
  selectedDatabases: string[];
  onChange: (databases: string[]) => void;
  onClose: () => void;
};

const DatabasePicker = ({
  noun,
  availableDatabases,
  availableLoading,
  availableError,
  availableWarning,
  onRetry,
  selectedDatabases,
  onChange,
  onClose,
}: DatabasePickerProps) => {
  const Noun = toTitleCase(noun);
  return (
    <Column gap={1}>
      <Text fontWeight="medium">{Noun} to share</Text>
      <Text size="sm" color="text.secondary">
        Choose the {noun} Hightouch should create a zero-copy share of. {Noun}{" "}
        you already share are omitted.
      </Text>
      {availableError ? (
        <RetryableAlert
          title={`Couldn’t load ${noun}`}
          message={availableError}
          onRetry={onRetry}
        />
      ) : (
        <>
          {availableWarning && (
            <RetryableAlert
              title={`Couldn’t refresh ${noun}`}
              message={availableWarning}
              onRetry={onRetry}
            />
          )}
          <MultiSelect
            placeholder={`Select ${noun} to share`}
            isLoading={availableLoading}
            options={availableDatabases}
            optionLabel={(option) => option}
            optionValue={(option) => option}
            value={selectedDatabases}
            onChange={(value) => onChange(value ?? [])}
            onClose={onClose}
            width="100%"
          />
        </>
      )}
    </Column>
  );
};

type MissingRegionAlertProps = {
  sourceId: string;
  permission: ComponentProps<typeof PermissionedButton>["permission"];
};

/**
 * Only schema discovery writes cloud + region to `warehouse_info` — a source
 * test can't, since it builds an unsaved source. So offer a refresh rather
 * than a re-check; discovery invalidates the cache, refetching eligibility.
 */
const MissingRegionAlert = ({
  sourceId,
  permission,
}: MissingRegionAlertProps) => {
  const { discovering, runSourceDiscovery, discoverError } = useSchemaDiscovery(
    { connectionId: sourceId },
  );

  return (
    <Alert
      type="warning"
      title="Warehouse region unknown"
      message={
        <Column gap={1}>
          <Text>
            Zero-copy sharing needs a known warehouse cloud and region on this
            source. Refresh the schema to detect them.
          </Text>
          {discoverError && (
            <Text>
              Couldn’t refresh the schema: {toErrorMessage(discoverError)}
            </Text>
          )}
        </Column>
      }
      actions={
        <PermissionedButton
          permission={permission}
          variant="secondary"
          isLoading={discovering}
          onClick={async () => {
            try {
              await runSourceDiscovery();
            } catch (error) {
              // Rendered above via discoverError — report it, don't double-surface.
              captureException(error);
            }
          }}
        >
          Refresh schema
        </PermissionedButton>
      }
    />
  );
};

type RetryableAlertProps = {
  title: string;
  message: string;
  onRetry: () => void;
};

const RetryableAlert = ({ title, message, onRetry }: RetryableAlertProps) => (
  <Alert
    type="error"
    title={title}
    message={message}
    actions={
      <Button variant="secondary" onClick={onRetry}>
        Retry
      </Button>
    }
  />
);
